import React, { useRef, useState } from "react";
import { Camera, Shirt, Shield, X, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * OnboardingBanner — banner mostrato al fantallenatore approvato che non ha ancora
 * caricato tutti i suoi asset di identità (stemma, maglia, foto). Ogni "tile" è
 * cliccabile: apre un input file e uploada immediatamente. Il banner si nasconde
 * automaticamente quando tutti i 3 asset sono presenti.
 *
 * L'utente può anche chiudere temporaneamente il banner con la X — la scelta
 * viene persistita in sessionStorage (torna al prossimo login).
 */
export default function OnboardingBanner({ team, onUpdated }) {
  const shieldRef = useRef(null);
  const jerseyRef = useRef(null);
  const coachRef = useRef(null);
  const [uploading, setUploading] = useState(null); // "shield" | "jersey" | "coach" | null
  const [hidden, setHidden] = useState(() =>
    typeof window !== "undefined" && sessionStorage.getItem("fife:onboarding_hidden") === "1"
  );

  const missing = {
    shield: !team?.shield_path,
    jersey: !team?.jersey_path,
    coach: !team?.coach_path,
  };
  const allDone = !missing.shield && !missing.jersey && !missing.coach;
  if (allDone || hidden) return null;

  const upload = async (kind, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Serve un file immagine");
    const endpoints = { shield: "/me/shield", jersey: "/me/jersey", coach: "/me/coach-photo" };
    const labels = { shield: "Stemma", jersey: "Maglia", coach: "Foto fantallenatore" };
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(endpoints[kind], fd);
      toast.success(`${labels[kind]} caricato!`);
      onUpdated && (await onUpdated());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fallito");
    } finally {
      setUploading(null);
    }
  };

  const dismissTemporarily = () => {
    sessionStorage.setItem("fife:onboarding_hidden", "1");
    setHidden(true);
  };

  const totalMissing = Object.values(missing).filter(Boolean).length;

  return (
    <div
      className="relative overflow-hidden rounded-3xl fade-up"
      style={{
        background: "linear-gradient(135deg, #3b56e0 0%, #6366f1 45%, #f97316 100%)",
      }}
      data-testid="onboarding-banner"
    >
      <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full blur-3xl opacity-40 pointer-events-none"
           style={{ background: "#facc15" }} />
      <button
        onClick={dismissTemporarily}
        className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 text-white/80 hover:text-white flex items-center justify-center transition-colors z-10"
        aria-label="Nascondi banner"
        data-testid="onboarding-dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="relative p-5 md:p-6">
        <div className="flex items-center gap-2 text-white/90 mb-1">
          <Sparkles className="w-4 h-4" style={{ color: "#facc15" }} />
          <span className="text-[10px] uppercase tracking-widest font-black">Completa la tua identità</span>
        </div>
        <h2 className="display font-black text-white text-2xl md:text-3xl leading-tight">
          Ti mancano <span style={{ color: "#facc15" }}>{totalMissing}</span> immagin{totalMissing === 1 ? "e" : "i"} squadra
        </h2>
        <p className="text-white/80 text-sm mt-1 max-w-lg">
          Carica stemma, maglia e foto del fantallenatore per far vivere la tua squadra in tutta la piattaforma.
        </p>

        <div className="mt-5 grid grid-cols-3 gap-2 md:gap-3">
          {[
            { kind: "shield", label: "Stemma", icon: Shield, ref: shieldRef },
            { kind: "jersey", label: "Maglia", icon: Shirt, ref: jerseyRef },
            { kind: "coach", label: "Foto Mister", icon: Camera, ref: coachRef },
          ].map(({ kind, label, icon: Icon, ref }) => {
            const done = !missing[kind];
            const busy = uploading === kind;
            return (
              <button
                key={kind}
                type="button"
                onClick={() => !done && !busy && ref.current?.click()}
                disabled={done || busy}
                className={`group relative flex flex-col items-center justify-center gap-2 p-3 md:p-4 rounded-2xl border-2 border-dashed transition-all ${
                  done
                    ? "bg-white/10 border-white/20 cursor-default"
                    : "bg-white/15 border-white/40 hover:bg-white/25 hover:border-white/70 hover:-translate-y-0.5 cursor-pointer"
                } disabled:cursor-not-allowed`}
                data-testid={`onboarding-${kind}-btn`}
              >
                <div className={`w-9 h-9 md:w-11 md:h-11 rounded-full flex items-center justify-center ${
                  done ? "bg-emerald-400 text-emerald-900" : "bg-white text-slate-900"
                }`}>
                  {busy ? (
                    <span className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                  ) : done ? (
                    <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor"><path d="M7.5 13.5L4 10l1.4-1.4 2.1 2.1L14.6 3.6 16 5z"/></svg>
                  ) : (
                    <Icon className="w-4 h-4 md:w-5 md:h-5" />
                  )}
                </div>
                <span className="text-white text-[11px] md:text-xs font-black uppercase tracking-widest text-center leading-tight">
                  {done ? `${label} ✓` : label}
                </span>
                <input
                  ref={ref}
                  type="file"
                  accept="image/*"
                  onChange={(e) => { upload(kind, e.target.files?.[0]); e.target.value = ""; }}
                  className="hidden"
                  data-testid={`onboarding-${kind}-input`}
                />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
